import React, { useState } from "react";
import AdminSectionHeader from "@/components/admin/AdminSectionHeader";
import AppTabs from "@/components/app/AppTabs";
import { BrandLogo, BrandMark, tokens, icons } from "@/brand";
import AdminBrandTheme from "./AdminBrandTheme";
import { ExternalLink } from "lucide-react";

const TABS = [
    { key: "overview",   label: "Vue d’ensemble" },
    { key: "logo",       label: "Logo" },
    { key: "icons",      label: "Icônes" },
    { key: "palette",    label: "Palette" },
    { key: "typography", label: "Typographie" },
    { key: "spacing",    label: "Espace & rayons" },
    { key: "og",         label: "OpenGraph" },
    { key: "pdf",        label: "PDF" },
    { key: "seo",        label: "SEO" },
    { key: "editor",     label: "Éditeur palette" },
];

export default function AdminBrand() {
    const [tab, setTab] = useState("overview");
    return (
        <>
            <AdminSectionHeader
                title="Brand System & Guidelines"
                description="Documentation officielle Mission Dresseur — single source of truth (tokens.json) consommée par chaque surface."
            />
            <div className="mb-6">
                <AppTabs items={TABS} value={tab} onChange={setTab} testId="brand-tabs" />
            </div>
            <div className="space-y-8">
                {tab === "overview"   && <Overview />}
                {tab === "logo"       && <LogoGuidelines />}
                {tab === "icons"      && <IconRegistry />}
                {tab === "palette"    && <PaletteDocs />}
                {tab === "typography" && <TypographyDocs />}
                {tab === "spacing"    && <SpacingDocs />}
                {tab === "og"         && <OgDocs />}
                {tab === "pdf"        && <PdfDocs />}
                {tab === "seo"        && <SeoDocs />}
                {tab === "editor"     && <AdminBrandTheme />}
            </div>
        </>
    );
}

/* ============================================================ */
/*  PRIMITIVES                                                  */
/* ============================================================ */

function Card({ children, dark, className = "" }) {
    const bg = dark ? "bg-md-primary text-md-primary-fg border-md-primary" : "bg-md-surface text-md-text border-md-border";
    return <div className={`rounded-2xl border ${bg} ${className}`}>{children}</div>;
}

function Section({ title, subtitle, eyebrow, children, id }) {
    return (
        <section id={id} className="scroll-mt-20">
            {eyebrow && <p className="md-overline mb-2">{eyebrow}</p>}
            <h2 className="text-2xl font-black tracking-tight text-md-text sm:text-3xl">{title}</h2>
            {subtitle && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-md-text-2">{subtitle}</p>}
            <div className="mt-6">{children}</div>
        </section>
    );
}

function Token({ name, value, mono = true }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-md-border bg-md-bg px-3 py-2 text-xs">
            <span className="font-semibold text-md-text">{name}</span>
            <span className={`text-md-text-2 ${mono ? "font-mono" : ""}`}>{value}</span>
        </div>
    );
}

function Pill({ tone = "neutral", children }) {
    const map = {
        ok: "bg-md-accent/15 text-md-accent border-md-accent/30",
        ko: "bg-md-danger/10 text-md-danger border-md-danger/30",
        neutral: "bg-md-surface-2 text-md-text-2 border-md-border",
        gold: "bg-md-secondary/15 text-md-secondary border-md-secondary/30",
    };
    return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${map[tone]}`}>{children}</span>;
}

/* ============================================================ */
/*  TABS                                                         */
/* ============================================================ */

function Overview() {
    return (
        <Section eyebrow="DOCUMENTATION OFFICIELLE" title="Brand System Mission Dresseur" subtitle="Une plateforme premium, calme, intemporelle. La documentation ci-dessous consomme directement les tokens, SVG et composants réels — toute évolution dans tokens.json se reflète immédiatement ici.">
            <div className="grid gap-4 lg:grid-cols-3">
                <Card>
                    <div className="p-6">
                        <BrandLogo variant="horizontal" size="lg" to={null} />
                        <p className="mt-6 text-sm leading-relaxed text-md-text-2">{tokens.brand.doctrine}</p>
                    </div>
                </Card>
                <Card>
                    <div className="p-6">
                        <p className="md-overline">RÈGLES D’USAGE</p>
                        <ul className="mt-3 space-y-2 text-sm text-md-text">
                            <li>• <span className="font-semibold">Inter</span> uniquement (Black / Bold / Medium / Regular)</li>
                            <li>• Aucune couleur inline — tout passe par <code className="font-mono text-xs">tokens.json</code></li>
                            <li>• Un seul logo, exposé via <code className="font-mono text-xs">@/brand</code></li>
                            <li>• Aucun SVG dupliqué dans les pages</li>
                            <li>• Vocabulaire doctrinaire (mission, classeur, dresseur, doublons)</li>
                        </ul>
                    </div>
                </Card>
                <Card>
                    <div className="p-6">
                        <p className="md-overline">INTERDICTIONS</p>
                        <ul className="mt-3 space-y-2 text-sm text-md-text">
                            <li>• Gradients criards / saturation gamer</li>
                            <li>• Émojis comme icônes (utiliser le registry)</li>
                            <li>• Recréer une variante locale du logo</li>
                            <li>• Mentionner « panier », « commande », « top seller », « commission »</li>
                            <li>• Hardcoder un hex dans un composant</li>
                        </ul>
                    </div>
                </Card>
            </div>

            <div className="mt-8 md-doctrine">
                <p className="md-overline mb-2">DOCTRINE</p>
                <p className="md-pullquote">{tokens.brand.doctrine}</p>
            </div>
        </Section>
    );
}

function LogoGuidelines() {
    const variants = ["horizontal", "compact", "icon", "wordmark"];
    const sizes = ["sm", "md", "lg", "xl"];
    return (
        <Section eyebrow="LOGO SYSTEM" title="Variantes & règles d’usage" subtitle="Mark canonique (diamant 45°, facette champagne = révélation). Le mark, le wordmark et le lockup sont tous dérivés du même composant React — jamais de SVG inline.">
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <div className="p-6">
                        <p className="md-overline">SUR FOND CLAIR (ink)</p>
                        <div className="mt-6 flex flex-col gap-6">
                            {variants.map((v) => (
                                <div key={v} className="flex items-center justify-between gap-4 border-b border-md-border pb-4 last:border-b-0 last:pb-0">
                                    <BrandLogo variant={v} size="lg" tone="ink" to={null} testId={`bg-logo-ink-${v}`} />
                                    <Pill tone="neutral">{v}</Pill>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
                <Card dark>
                    <div className="p-6">
                        <p className="md-overline" style={{ color: "var(--md-secondary)" }}>SUR FOND SOMBRE (cream)</p>
                        <div className="mt-6 flex flex-col gap-6">
                            {variants.map((v) => (
                                <div key={v} className="flex items-center justify-between gap-4 border-b border-md-primary-fg/15 pb-4 last:border-b-0 last:pb-0">
                                    <BrandLogo variant={v} size="lg" tone="cream" to={null} testId={`bg-logo-cream-${v}`} />
                                    <Pill tone="gold">{v}</Pill>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-2">
                <Card>
                    <div className="p-6">
                        <p className="md-overline">TAILLES — ÉCHELLE</p>
                        <div className="mt-4 flex flex-wrap items-end gap-6">
                            {sizes.map((s) => (
                                <div key={s} className="flex flex-col items-center gap-2">
                                    <BrandLogo variant="icon" size={s} tone="ink" to={null} />
                                    <Pill tone="neutral">{s}</Pill>
                                </div>
                            ))}
                        </div>
                        <p className="mt-4 text-xs text-md-text-2">Taille minimale : <span className="font-mono">16 px</span> (favicon). En dessous : remplacer par mark uniquement.</p>
                    </div>
                </Card>
                <Card>
                    <div className="p-6">
                        <p className="md-overline">CLEAR-SPACE</p>
                        <div className="mt-4 rounded-xl border border-dashed border-md-border bg-md-bg p-8">
                            <div className="rounded-md border border-md-border bg-md-surface p-6">
                                <BrandLogo variant="horizontal" size="md" tone="ink" to={null} />
                            </div>
                        </div>
                        <p className="mt-4 text-xs text-md-text-2">Garder au minimum la largeur du mark autour du lockup.</p>
                    </div>
                </Card>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-2">
                <Card>
                    <div className="p-6">
                        <p className="md-overline" style={{ color: "var(--md-accent)" }}>✓ AUTORISÉ</p>
                        <ul className="mt-3 space-y-2 text-sm text-md-text">
                            <li>• Mark seul à favicon, splash, OG, avatar</li>
                            <li>• Lockup horizontal en navbar desktop</li>
                            <li>• Lockup compact en navbar mobile</li>
                            <li>• Print tone pour PDF / impression</li>
                        </ul>
                    </div>
                </Card>
                <Card>
                    <div className="p-6">
                        <p className="md-overline" style={{ color: "var(--md-danger)" }}>✗ INTERDIT</p>
                        <ul className="mt-3 space-y-2 text-sm text-md-text">
                            <li>• Modifier les proportions ou écraser le mark</li>
                            <li>• Repeindre la facette champagne dans une autre couleur</li>
                            <li>• Recréer une variante locale (toujours via <code className="font-mono text-xs">@/brand</code>)</li>
                            <li>• Mettre un dégradé ou un effet sur le mark</li>
                            <li>• Combiner avec une police autre qu’Inter</li>
                        </ul>
                    </div>
                </Card>
            </div>
        </Section>
    );
}

function IconRegistry() {
    const families = [
        { title: "BrandIcon",      desc: "Symboles propriétaires Mission Dresseur.", entries: icons.BrandIcon, accent: "var(--md-secondary)" },
        { title: "NavigationIcon", desc: "Navigation, sidebar, onboarding.",         entries: icons.NavigationIcon },
        { title: "ActionIcon",     desc: "Déclencheurs : créer, modifier, supprimer.", entries: icons.ActionIcon },
        { title: "StatusIcon",     desc: "Feedback : succès, erreur, attention.",   entries: icons.StatusIcon },
        { title: "DocumentIcon",   desc: "Fichiers, PDF, exports.",                  entries: icons.DocumentIcon },
        { title: "SocialIcon",     desc: "Réseaux externes.",                         entries: icons.SocialIcon },
        { title: "ThemeIcon",      desc: "Bascule jour / nuit.",                      entries: icons.ThemeIcon },
    ];

    return (
        <Section eyebrow="ICON REGISTRY" title="Sept familles classifiées" subtitle="Toutes les icônes consommées dans l’app, le PWA et l’admin proviennent de @/brand/icons. Aucune importation directe depuis lucide-react.">
            {families.map((fam) => (
                <Card key={fam.title} className="mb-6">
                    <div className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="md-overline" style={fam.accent ? { color: fam.accent } : undefined}>{fam.title}</p>
                                <p className="mt-1 text-xs text-md-text-2">{fam.desc}</p>
                            </div>
                            <Pill tone="neutral">{Object.keys(fam.entries).length} icônes</Pill>
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
                            {Object.entries(fam.entries).map(([name, Icon]) => (
                                <div key={name} className="flex flex-col items-center gap-2 rounded-lg border border-md-border bg-md-bg px-2 py-3">
                                    <Icon className="h-5 w-5 text-md-text" />
                                    <span className="truncate text-[10px] font-mono text-md-text-2">{name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            ))}
        </Section>
    );
}

function PaletteDocs() {
    const lightRoles = ["bg", "bg-2", "surface", "surface-2", "primary", "primary-fg", "secondary", "secondary-fg", "accent", "success", "warning", "danger", "text", "text-2", "border"];
    const rarities = Object.entries(tokens.rarity);
    return (
        <Section eyebrow="PALETTE" title="Couleurs & rôles sémantiques" subtitle="Chaque rôle s’adapte au mode light/dark. Les hex sont stockés dans tokens.json — l’admin peut les éditer via l’onglet « Éditeur palette ».">
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <div className="p-6">
                        <p className="md-overline">CŒUR ÉDITORIAL</p>
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            {[["navy", tokens.core.navy.hex], ["champagne", tokens.core.champagne.hex], ["cream", tokens.core.cream.hex], ["sage", tokens.core.sage.hex]].map(([name, hex]) => (
                                <div key={name} className="rounded-xl border border-md-border bg-md-bg p-3">
                                    <div className="h-16 rounded-md" style={{ background: hex }} />
                                    <p className="mt-2 text-xs font-semibold text-md-text">{name}</p>
                                    <p className="font-mono text-[10px] text-md-text-2">{hex}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>

                <Card>
                    <div className="p-6">
                        <p className="md-overline">RÔLES SÉMANTIQUES (Light)</p>
                        <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {lightRoles.map((r) => (
                                <div key={r} className="flex items-center gap-3 rounded-lg border border-md-border bg-md-bg p-2.5">
                                    <span className="h-8 w-8 flex-none rounded" style={{ background: `hsl(var(--md-${r}))`, border: "1px solid hsl(var(--md-border))" }} />
                                    <div className="min-w-0">
                                        <p className="text-xs font-semibold text-md-text">--md-{r}</p>
                                        <p className="font-mono text-[10px] text-md-text-2">hsl var</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Card>
            </div>

            <Card className="mt-6">
                <div className="p-6">
                    <p className="md-overline">RARETÉS (mission categories)</p>
                    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                        {rarities.map(([slug, data]) => (
                            <div key={slug} className="rounded-xl border border-md-border bg-md-bg p-3">
                                <div className="h-12 rounded-md" style={{ background: data.hex }} />
                                <p className="mt-2 text-xs font-semibold text-md-text">{data.label}</p>
                                <p className="font-mono text-[10px] text-md-text-2">{data.hex}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        </Section>
    );
}

function TypographyDocs() {
    const styles = tokens.typography.styles;
    return (
        <Section eyebrow="TYPOGRAPHIE" title="Échelle éditoriale Inter" subtitle="Une seule famille pour toute la marque. Les poids Black / Bold / Medium / Regular sont chargés via Google Fonts ; aucune autre police n’est autorisée nulle part.">
            <Card>
                <div className="space-y-6 p-6">
                    {Object.entries(styles).map(([key, st]) => (
                        <div key={key} className="border-b border-md-border pb-5 last:border-b-0 last:pb-0">
                            <div className="flex flex-wrap items-baseline justify-between gap-3">
                                <p style={{ fontSize: st.size, lineHeight: st.lh, fontWeight: st.weight, letterSpacing: st.tracking, textTransform: st.transform || "none" }} className="text-md-text">
                                    Mission Dresseur — {key}
                                </p>
                                <div className="flex gap-2">
                                    <Pill tone="neutral">{st.size}</Pill>
                                    <Pill tone="neutral">w{st.weight}</Pill>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </Section>
    );
}

function SpacingDocs() {
    const spaces = Object.entries(tokens.space);
    const radii = Object.entries(tokens.radius);
    return (
        <Section eyebrow="ESPACES & RAYONS" title="Rythme & géométrie" subtitle="L’échelle d’espaces suit une base 4. Les rayons donnent la respiration éditoriale ; les ombres sont calmes.">
            <Card className="mb-6">
                <div className="p-6">
                    <p className="md-overline">SPACING</p>
                    <div className="mt-5 space-y-3">
                        {spaces.map(([name, val]) => (
                            <div key={name} className="flex items-center gap-4">
                                <span className="w-12 font-mono text-xs text-md-text-2">{name}</span>
                                <div className="h-3 rounded-sm bg-md-secondary" style={{ width: val }} />
                                <span className="font-mono text-xs text-md-text">{val}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>

            <Card>
                <div className="p-6">
                    <p className="md-overline">RADIUS</p>
                    <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
                        {radii.map(([name, val]) => (
                            <div key={name} className="rounded-md border border-md-border bg-md-bg p-3 text-center">
                                <div className="mx-auto h-16 w-16 border border-md-secondary bg-md-secondary/15" style={{ borderRadius: val }} />
                                <p className="mt-2 text-xs font-semibold text-md-text">{name}</p>
                                <p className="font-mono text-[10px] text-md-text-2">{val}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </Card>
        </Section>
    );
}

function OgDocs() {
    const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api/og";
    const previews = [
        { template: "default", title: "Bienvenue dans la mission",      subtitle: "Aventure collaborative pour collectionneurs Pokémon", eyebrow: "DOCTRINE" },
        { template: "mission", title: "Mon classeur SV",                stat1_label: "Cartes", stat1_value: "47", stat2_label: "Estimation", stat2_value: "12,40 €", eyebrow: "MISSION EN PRÉPARATION" },
    ];
    return (
        <Section eyebrow="OPENGRAPH" title="Preview dynamiques" subtitle="Chaque page indexable doit déclarer une og:image générée à la volée. L’endpoint accepte title/subtitle/eyebrow/stat1_*/stat2_* ; nouvelle template = nouveau .svg dans /app/backend/brand/og_templates.">
            <div className="space-y-6">
                {previews.map((p) => {
                    const url = `${base}/${p.template}?` + new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([k]) => k !== "template"))).toString();
                    return (
                        <Card key={p.template}>
                            <div className="p-6">
                                <div className="flex items-center justify-between">
                                    <p className="md-overline">TEMPLATE — {p.template}</p>
                                    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-md-secondary hover:underline" data-testid={`og-link-${p.template}`}>
                                        Ouvrir <ExternalLink className="h-3 w-3" />
                                    </a>
                                </div>
                                <div className="mt-4 overflow-hidden rounded-xl border border-md-border bg-md-bg">
                                    <img src={url} alt={`OG ${p.template}`} loading="lazy" className="w-full" />
                                </div>
                                <p className="mt-3 break-all font-mono text-[10px] text-md-text-2">{url}</p>
                            </div>
                        </Card>
                    );
                })}
                <Card>
                    <div className="p-6">
                        <p className="md-overline">PROCHAINES TEMPLATES</p>
                        <p className="mt-2 text-sm text-md-text-2">card · set · article — architecture déjà prête, à livrer en suite.</p>
                    </div>
                </Card>
            </div>
        </Section>
    );
}

function PdfDocs() {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return (
        <Section eyebrow="PDF DOCTRINE" title="Système documentaire premium" subtitle="Les PDF (doctrine, onboarding, guides, decks) consomment doctrine_style.css, lui-même dérivé de tokens.json. Régénération via python scripts/doctrine/build_pdf.py.">
            <Card>
                <div className="p-6">
                    <p className="md-overline">DOCTRINE OFFICIELLE — v1</p>
                    <p className="mt-2 text-sm text-md-text-2">Le manifeste, la doctrine produit et les 25 chapitres de référence — utilisée comme source IA, brief équipe et page partenaire.</p>
                    <div className="mt-5 flex flex-wrap gap-3">
                        <a href={`${base}/api/public/doctrine.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-md-primary px-4 py-2 text-xs font-semibold text-md-primary-fg hover:opacity-90" data-testid="pdf-doctrine-open">
                            Ouvrir la doctrine PDF <ExternalLink className="h-3 w-3" />
                        </a>
                    </div>
                </div>
            </Card>
            <Card className="mt-6">
                <div className="p-6">
                    <p className="md-overline">RÈGLES DOCTRINAIRES</p>
                    <ul className="mt-3 space-y-2 text-sm text-md-text">
                        <li>• Aucune couleur inline — uniquement des variables CSS (mappées sur tokens.json).</li>
                        <li>• Inter uniquement, Black/Bold/Medium/Regular.</li>
                        <li>• Tout bloc déclare <code className="font-mono text-xs">page-break-inside: avoid</code>.</li>
                        <li>• Composants normalisés : .pdf-cover / .pdf-hero / .pdf-section / .pdf-callout / .pdf-table / .pdf-grid.</li>
                    </ul>
                </div>
            </Card>
        </Section>
    );
}

function SeoDocs() {
    return (
        <Section eyebrow="SEO / LLM" title="Architecture méta indexable" subtitle="Chaque page utilise le composant <SEO /> qui injecte title, meta, og:*, twitter:card et JSON-LD. Compatible Google Discover, ChatGPT/Gemini/Perplexity, Discord, X, Telegram.">
            <div className="grid gap-4 lg:grid-cols-2">
                <Card>
                    <div className="p-6">
                        <p className="md-overline">META INJECTÉES</p>
                        <ul className="mt-3 space-y-2 text-sm text-md-text">
                            <li>• <code className="font-mono text-xs">&lt;title&gt;</code> + <code className="font-mono text-xs">meta description</code></li>
                            <li>• <code className="font-mono text-xs">og:site_name / locale / type / title / description / image / url</code></li>
                            <li>• <code className="font-mono text-xs">twitter:card=summary_large_image</code></li>
                            <li>• <code className="font-mono text-xs">link rel="canonical"</code></li>
                            <li>• <code className="font-mono text-xs">theme-color</code> = navy <span className="font-mono text-xs">#0B1021</span></li>
                            <li>• JSON-LD <code className="font-mono text-xs">Organization</code> + <code className="font-mono text-xs">WebSite</code> globaux + schema par page</li>
                        </ul>
                    </div>
                </Card>
                <Card>
                    <div className="p-6">
                        <p className="md-overline">USAGE</p>
                        <pre className="mt-3 overflow-x-auto rounded-lg bg-md-primary p-4 text-[11px] leading-relaxed text-md-primary-fg"><code>{`<SEO
  title="Mon classeur SV"
  description="..."
  canonical="https://missiondresseur.fr/.../"
  og={{ template: "mission",
        title: "Mon classeur SV",
        stat1_label: "Cartes",
        stat1_value: "47",
        stat2_label: "Estimation",
        stat2_value: "12,40 €" }}
  schema={{
    "@context": "https://schema.org",
    "@type": "Article", ...
  }}
/>`}</code></pre>
                    </div>
                </Card>
            </div>
            <Card className="mt-6">
                <div className="p-6">
                    <p className="md-overline">CONTRAT LLM-FRIENDLY</p>
                    <ul className="mt-3 space-y-2 text-sm text-md-text">
                        <li>• HTML sémantique (header / main / article / section / nav)</li>
                        <li>• Vocabulaire doctrinaire stable (mission, classeur, dresseur, doublons)</li>
                        <li>• Aucun contenu indexable en client-only — SSR/SSG cible à long terme</li>
                        <li>• Résumé sémantique en tête de page, FAQ blocks structurés</li>
                    </ul>
                </div>
            </Card>
        </Section>
    );
}
